import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/api";

export const fetchContent = createAsyncThunk(
  "content/fetchContent",
  async (sellerId, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.get(`/links-content/${sellerId}`, {
        withCredentials: true,
      });
      return fulfillWithValue(data.linksContent);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateContent = createAsyncThunk(
  "content/updateContent",
  async ({ sellerId, tab, content }, { rejectWithValue, fulfillWithValue }) => {
    try {
      // Convert tab name to the format expected by backend
      const tabMapping = {
        "About our Shop": "aboutShop",
        "Delivery Information": "deliveryInfo",
        "Privacy Policy": "privacyPolicy",
        "Terms and Conditions": "termsAndConditions",
        "Shipping Policy": "shippingPolicy",
        "Return Policy": "returnPolicy",
        "Blogs": "blogs"
      };

      const backendTab = tabMapping[tab];
      if (!backendTab) {
        throw new Error("Invalid tab name");
      }

      const { data } = await api.put(
        `/links-content/${sellerId}`,
        { 
          linksContent: {
            [backendTab]: content
          }
        },
        {
          withCredentials: true,
        }
      );
      return fulfillWithValue(data.linksContent);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteContent = createAsyncThunk(
  "content/deleteContent",
  async ({ sellerId, tab }, { rejectWithValue, fulfillWithValue }) => {
    try {
      // Convert tab name to the format expected by backend
      const tabMapping = {
        "About our Shop": "aboutShop",
        "Delivery Information": "deliveryInfo",
        "Privacy Policy": "privacyPolicy",
        "Terms and Conditions": "termsAndConditions",
        "Shipping Policy": "shippingPolicy",
        "Return Policy": "returnPolicy",
        "Blogs": "blogs"
      };

      const backendTab = tabMapping[tab];
      if (!backendTab) {
        throw new Error("Invalid tab name");
      }

      const { data } = await api.put(
        `/links-content/${sellerId}`,
        { 
          linksContent: {
            [backendTab]: ""
          }
        },
        {
          withCredentials: true,
        }
      );
      return fulfillWithValue({ tab, message: data.message });
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const linksReducer = createSlice({
  name: "content",
  initialState: {
    tabContent: {},
    activeTab: "About our Shop",
    isAdmin: false,
    editContent: "",
    status: "idle",
    error: null,
    successMessage: "",
    errorMessage: "",
    loader: false,
  },
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      state.editContent = state.tabContent[action.payload] || "";
    },
    toggleAdminMode: (state) => {
      state.isAdmin = !state.isAdmin;
    },
    setEditContent: (state, action) => {
      state.editContent = action.payload;
    },
    messageClear: (state) => {
      state.errorMessage = "";
      state.successMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContent.pending, (state) => {
        state.status = "loading";
        state.loader = true;
      })
      .addCase(fetchContent.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loader = false;
        // Convert backend format to frontend format
        const tabMapping = {
          "aboutShop": "About our Shop",
          "deliveryInfo": "Delivery Information",
          "privacyPolicy": "Privacy Policy",
          "termsAndConditions": "Terms and Conditions",
          "shippingPolicy": "Shipping Policy",
          "returnPolicy": "Return Policy",
          "blogs": "Blogs"
        };
        
        const frontendContent = {};
        Object.entries(action.payload).forEach(([key, value]) => {
          if (tabMapping[key]) {
            frontendContent[tabMapping[key]] = value;
          }
        });
        
        state.tabContent = frontendContent;
      })
      .addCase(fetchContent.rejected, (state, action) => {
        state.status = "failed";
        state.loader = false;
        state.error = action.error.message;
        state.errorMessage = "Failed to fetch content";
      })
      .addCase(updateContent.pending, (state) => {
        state.loader = true;
      })
      .addCase(updateContent.fulfilled, (state, action) => {
        // Convert backend format to frontend format
        const tabMapping = {
          "aboutShop": "About our Shop",
          "deliveryInfo": "Delivery Information",
          "privacyPolicy": "Privacy Policy",
          "termsAndConditions": "Terms and Conditions",
          "shippingPolicy": "Shipping Policy",
          "returnPolicy": "Return Policy",
          "blogs": "Blogs"
        };
        
        const frontendContent = {};
        Object.entries(action.payload).forEach(([key, value]) => {
          if (tabMapping[key]) {
            frontendContent[tabMapping[key]] = value;
          }
        });
        
        state.tabContent = { ...state.tabContent, ...frontendContent };
        state.loader = false;
        state.successMessage = "Content updated successfully";
      })
      .addCase(updateContent.rejected, (state, action) => {
        state.loader = false;
        state.errorMessage = "Failed to update content";
        state.error = action.error.message;
      })
      .addCase(deleteContent.pending, (state) => {
        state.loader = true;
      })
      .addCase(deleteContent.fulfilled, (state, action) => {
        const { tab } = action.payload;
        const newContent = { ...state.tabContent };
        delete newContent[tab];
        state.tabContent = newContent;
        state.loader = false;
        state.successMessage = "Content deleted successfully";
      })
      .addCase(deleteContent.rejected, (state, action) => {
        state.loader = false;
        state.errorMessage = "Failed to delete content";
        state.error = action.error.message;
      });
  },
});

export const { setActiveTab, toggleAdminMode, setEditContent, messageClear } =
  linksReducer.actions;

export default linksReducer.reducer;
